import { useState } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { MultiUserSelector } from "@crm-src/components/multi-user-selector"
import { Users } from "lucide-react"

interface Reseller {
  id: string | number
  name: string
  code?: string
}

interface BulkAssignSalespeopleModalProps {
  isOpen: boolean
  onClose: () => void
  selectedResellers: Reseller[]
  onConfirmAssign?: (resellerIds: string[], salespeopleIds: string[]) => Promise<void>
}

export function BulkAssignSalespeopleModal({
  isOpen,
  onClose,
  selectedResellers,
  onConfirmAssign
}: BulkAssignSalespeopleModalProps) {
  const { toast } = useToast()
  const [isAssigning, setIsAssigning] = useState(false)
  const [selectedSalespeopleIds, setSelectedSalespeopleIds] = useState<string[]>([])

  const handleAssign = async () => {
    if (!onConfirmAssign || selectedResellers.length === 0) return

    if (selectedSalespeopleIds.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one salesperson",
        variant: "destructive",
      })
      return
    }

    setIsAssigning(true)
    try {
      const resellerIds = selectedResellers.map(reseller => reseller.id.toString())
      await onConfirmAssign(resellerIds, selectedSalespeopleIds)
      setSelectedSalespeopleIds([])
      onClose()
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsAssigning(false)
    }
  }

  const handleClose = () => {
    if (!isAssigning) {
      setSelectedSalespeopleIds([])
      onClose()
    }
  }

  if (!isOpen || selectedResellers.length === 0) return null

  return (
    <Dialog open={isOpen} onClose={() => !isAssigning && handleClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              height: 40,
              width: 40,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              color: "primary.main",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
            }}
          >
            <Users size={20} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: "text.primary" }}>
              Assign Salespeople to {selectedResellers.length} Reseller{selectedResellers.length > 1 ? 's' : ''}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.disabled", mt: 0.25 }}>
              Select salespeople to assign to the selected resellers
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
              border: 1,
              borderColor: (t) => alpha(t.palette.primary.main, 0.3),
              borderRadius: 1,
              p: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box sx={{ color: "primary.main", mt: 0.25, flexShrink: 0, display: "flex" }}>
                <Users size={20} />
              </Box>
              <Box>
                <Typography component="h4" sx={{ fontSize: 14, fontWeight: 500, color: "primary.dark", mb: 0.5 }}>
                  Bulk Salesperson Assignment
                </Typography>
                <Typography sx={{ fontSize: 14, color: "primary.main", mb: 1 }}>
                  The selected salespeople will be assigned to all {selectedResellers.length} selected
                  reseller{selectedResellers.length > 1 ? 's' : ''} and all their associated customers and prospects.
                  Existing assignments will be replaced.
                </Typography>
                {selectedResellers.length <= 5 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: 12, color: "primary.main", fontWeight: 500, mb: 0.5 }}>Resellers:</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none", display: "grid", gap: 0.5, fontSize: 12, color: "primary.main" }}>
                      {selectedResellers.map((reseller) => (
                        <Box component="li" key={reseller.id} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          • {reseller.name}
                          {reseller.code && (
                            <Box component="span" sx={{ color: "primary.light" }}> (Code: {reseller.code})</Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Box>
            <MultiUserSelector
              value={selectedSalespeopleIds}
              onValueChange={setSelectedSalespeopleIds}
              label="Select Salespeople"
              placeholder="Select salespeople to assign..."
            />
            <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 1 }}>
              {selectedSalespeopleIds.length > 0
                ? `${selectedSalespeopleIds.length} salesperson${selectedSalespeopleIds.length > 1 ? 's' : ''} selected`
                : 'No salespeople selected yet'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={isAssigning}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={handleAssign}
          disabled={isAssigning || selectedSalespeopleIds.length === 0}
          sx={{ minWidth: 120 }}
          startIcon={
            isAssigning
              ? <CircularProgress size={16} color="inherit" />
              : <Users size={16} />
          }
        >
          {isAssigning ? "Assigning..." : "Assign Salespeople"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
